// client/src/pages/secure/TeamManagementPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom'; // Import Link
import { useApiClient } from '../../hooks/useApiClient';
import { getCurrentUser, canManageStaff } from '../../hooks/useCurrentUser';
import DataList from '../../components/shared/DataList';

// --- Add Team Modal Component ---
const AddTeamModal = ({ isOpen, onClose, onTeamAdded }) => {
    const [teamName, setTeamName] = useState('');
    const [description, setDescription] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const apiClient = useApiClient();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);
        try {
            const newTeam = await apiClient.post('/api/teams', { teamName, description });
            onTeamAdded(newTeam);
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to create team.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
            <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-lg">
                <h2 className="text-2xl font-bold text-slate-800 mb-6">Create New Team</h2>
                <form onSubmit={handleSubmit}>
                    <div className="space-y-4">
                        <input value={teamName} onChange={(e) => setTeamName(e.target.value)} placeholder="Team Name" required className="p-2 border rounded w-full" />
                        <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Team Description (Optional)" rows="3" className="p-2 border rounded w-full"></textarea>
                    </div>
                    {error && <p className="text-red-500 text-sm mt-4">{error}</p>}
                    <div className="flex justify-end gap-4 mt-6">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
                        <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-slate-800 text-white rounded disabled:bg-indigo-400">
                            {isSubmitting ? 'Creating...' : 'Create Team'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- Team Management Page Component ---
const TeamManagementPage = () => {
    const [teams, setTeams] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const apiClient = useApiClient();

    useEffect(() => {
        apiClient.get('/api/teams')
            .then(data => setTeams(data))
            .catch(err => setError(err.message || 'Failed to fetch teams.'))
            .finally(() => setIsLoading(false));
    }, [apiClient]);

    const handleTeamAdded = (newTeam) => {
        setTeams(prev => [...prev, newTeam].sort((a, b) => a.team_name.localeCompare(b.team_name)));
    };

    return (
        <>
            <AddTeamModal 
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onTeamAdded={handleTeamAdded}
            />
            <div className="bg-white p-8 rounded-lg shadow">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold text-slate-800">Team Management</h1>
                    {canManageStaff(getCurrentUser()) && (
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-md hover:bg-slate-900"
                        >
                            Create New Team
                        </button>
                    )}
                </div>

                {isLoading && <p>Loading teams...</p>}
                {error && <p className="text-red-500">{error}</p>}
                {!isLoading && !error && (
                    <div className="overflow-x-auto">
                        <div className="p-4"><DataList rows={teams} rowKey="team_id" csvName="teams" defaultSort={{ key: 'team_name', dir: 'asc' }} empty="No teams yet."
                            columns={[
                                { key: 'team_name', label: 'Team Name', render: (team) => <span className="font-medium text-slate-900">{team.team_name}</span> },
                                { key: 'member_count', label: 'Members', type: 'number' },
                                { key: 'actions', label: 'Actions', sortable: false, csv: false, search: false, render: (team) => <Link to={`/dashboard/teams/${team.team_id}`} className="text-indigo-600 hover:text-indigo-900">Manage Members</Link> },
                            ]} /></div>
                    </div>
                )}
            </div>
        </>
    );
};

export default TeamManagementPage;
